import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, Grid, Paper, TextField, InputAdornment, Accordion,
  AccordionSummary, AccordionDetails, List, ListItem, ListItemText,
  Checkbox, ListSubheader, Divider, ListItemIcon, Chip, Stack, FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  Business as BusinessIcon,
  Monitor as MonitorIcon,
  Scale as ScaleIcon,
  WebAsset as WebAssetIcon,
  Dns as DnsIcon, // Icona per Gruppi Dispositivo
  Widgets as WidgetsIcon, // Icona per Tipi Dispositivo
} from '@mui/icons-material';

/**
 * @constant DEVICE_TYPES
 * @description Array di oggetti che definiscono i tipi di dispositivo disponibili.
 * Ogni oggetto include un 'id', una 'description' testuale e un'icona Material-UI.
 * Queste informazioni vengono utilizzate per visualizzare i tipi di dispositivo nell'interfaccia utente.
 * @type {Array<Object>}
 * @example
 * // Utilizzo:
 * // DEVICE_TYPES.map(type => <Chip label={type.description} icon={type.icon} />)
 */
const DEVICE_TYPES = [
    { id: 1, description: 'Monitor', icon: <MonitorIcon /> },
    { id: 3, description: 'Bilancia', icon: <ScaleIcon /> },
    { id: 2, description: 'Totem', icon: <WebAssetIcon /> },
  ]

/**
 * @interface PlaylistProps
 * @description Interfaccia per le props del componente `Playlist`.
 * @property {Object} configuration - Oggetto di configurazione che contiene i dati necessari, come `devices`, `stores`, `storeGroups`, `deviceGroups`, `deviceTypes`.
 * @property {boolean} open - Booleano che indica se il Dialog è aperto o chiuso.
 * @property {function} onClose - Funzione di callback da richiamare quando il Dialog viene chiuso.
 * @property {function} onSave - Funzione di callback da richiamare quando i dispositivi selezionati vengono salvati. Riceve un oggetto `{ devices: Array<number> }` con gli ID dei dispositivi.
 * @property {number} selectedCount - Il numero di elementi già selezionati (ad esempio, il numero di playlist o contenuti da distribuire).
 */

/**
 * @component Playlist
 * @description Componente principale che gestisce la selezione e la distribuzione di dispositivi.
 * Permette agli utenti di filtrare un elenco di dispositivi per gruppo negozio, negozio, gruppo dispositivo e tipo dispositivo,
 * e di selezionare i dispositivi a cui distribuire uno o più elementi.
 * Utilizza componenti Material-UI per l'interfaccia utente (Dialog, Grid, Accordion, List, Checkbox, Chip, TextField, Button, ecc.).
 *
 * @param {PlaylistProps} props - Le proprietà passate al componente.
 * @returns {JSX.Element} Il componente Dialog per la selezione dei dispositivi.
 *
 * @example
 * // Nel componente padre:
 * <Playlist
 * configuration={{
 * devices: [{ id: 1, description: 'Dispositivo 1', store: 101, dtype: 1, dgroup: 1001 }],
 * stores: [{ id: 101, description: 'Negozio A', store_group: { id: 1, description: 'Gruppo Nord' } }],
 * storeGroups: [{ id: 1, description: 'Gruppo Nord' }],
 * deviceGroups: [{ id: 1001, description: 'Gruppo Dispositivi Ufficio' }],
 * deviceTypes: [{ id: 1, description: 'Monitor', icon: <MonitorIcon /> }]
 * }}
 * open={true}
 * onClose={() => console.log('Dialog chiuso')}
 * onSave={(data) => console.log('Dispositivi selezionati:', data.devices)}
 * selectedCount={3}
 * />
 */
export default function Playlist({ configuration, open, onClose, onSave, selectedCount, selectedItems = [] }) {
  // Aggiunge i tipi di dispositivo predefiniti alla configurazione se non sono già presenti.
  // Questo assicura che `configuration.deviceTypes` sia sempre disponibile per il rendering e i filtri.
  configuration["deviceTypes"] = DEVICE_TYPES;
  
  /**
   * @state filters
   * @description Stato che contiene i criteri di filtro correnti applicati ai dispositivi.
   * Include filtri per gruppi di negozi, negozi specifici, gruppi di dispositivi, tipi di dispositivi e un termine di ricerca testuale.
   * @type {Object}
   * @property {Array<number>} storeGroups - Array di ID dei gruppi di negozi selezionati.
   * @property {Array<number>} stores - Array di ID dei negozi selezionati.
   * @property {Array<number>} deviceGroups - Array di ID dei gruppi di dispositivi selezionati (NUOVO).
   * @property {Array<number>} deviceTypes - Array di ID dei tipi di dispositivi selezionati.
   * @property {string} searchTerm - Termine di ricerca testuale per nome dispositivo o nome negozio.
   * @example
   * // Esempio di stato `filters`:
   * {
   * storeGroups: [1, 2],
   * stores: [],
   * deviceGroups: [101],
   * deviceTypes: [1],
   * searchTerm: 'ufficio'
   * }
   */
  const [filters, setFilters] = useState({
    storeGroups: [],
    stores: [],
    deviceGroups: [], // NUOVO stato per il filtro gruppi dispositivo
    deviceTypes: [],
    searchTerm: '',
  });

  /**
   * @state selectedDevices
   * @description Stato che mantiene un array di ID dei dispositivi attualmente selezionati dall'utente.
   * Questi sono i dispositivi a cui verrà applicata la distribuzione.
   * @type {Array<number>}
   * @example
   * // Esempio di stato `selectedDevices`:
   * [101, 105, 203]
   */
  const [selectedDevices, setSelectedDevices] = useState([]);

  /**
   * @function availableDevices
   * @description Memoized selector che filtra l'elenco completo dei dispositivi in base ai `filters` correnti.
   * Utilizza `useMemo` per ricalcolare l'elenco solo quando i `filters` cambiano, ottimizzando le prestazioni.
   * I filtri vengono applicati in cascata: prima i gruppi di negozi, poi i negozi, poi i gruppi di dispositivi,
   * poi i tipi di dispositivi e infine il termine di ricerca.
   * @returns {Array<Object>} Un array di oggetti dispositivo che corrispondono ai criteri di filtro.
   * @example
   * // Se i filtri sono { storeGroups: [1], searchTerm: 'monitor' }
   * // `availableDevices` conterrà solo i monitor nei negozi appartenenti al gruppo 1.
   */
  const availableDevices = useMemo(() => {
    let devices = configuration.devices;
    
    // Applica filtri in cascata
    if (filters.storeGroups.length > 0) {
      // Trova tutti i negozi che appartengono ai gruppi di negozi selezionati
      const storesInGroup = configuration.stores
        .filter(s => filters.storeGroups.includes(s.store_group.id))
        .map(s => s.id);
      // Filtra i dispositivi per includere solo quelli nei negozi trovati
      devices = devices.filter(d => storesInGroup.includes(d.store));
    }
    if (filters.stores.length > 0) {
      // Filtra i dispositivi per includere solo quelli nei negozi specifici selezionati
      devices = devices.filter(d => filters.stores.includes(d.store));
    }
    // NUOVO: Logica di filtro per gruppi di dispositivi
    if (filters.deviceGroups.length > 0) {
      // Filtra i dispositivi per includere solo quelli nei gruppi di dispositivi selezionati
      devices = devices.filter(d => filters.deviceGroups.includes(d.dgroup));
    }
    if (filters.deviceTypes.length > 0) {
      // Filtra i dispositivi per includere solo quelli dei tipi di dispositivo selezionati
      devices = devices.filter(d => filters.deviceTypes.includes(d.dtype));
    }
    if (filters.searchTerm) {
      const lowerCaseSearch = filters.searchTerm.toLowerCase();
      // Trova gli ID dei negozi il cui nome corrisponde al termine di ricerca
      const storesByName = configuration.stores
        .filter(s => s.description.toLowerCase().includes(lowerCaseSearch))
        .map(s => s.id);
      // Filtra i dispositivi il cui nome o il cui negozio corrisponde al termine di ricerca
      devices = devices.filter(d =>
        d.description.toLowerCase().includes(lowerCaseSearch) || storesByName.includes(d.store)
      );
    }
    return devices;
  }, [filters, configuration.devices, configuration.stores]); // Dipendenze per useMemo

  /**
   * @function deviceTypeBreakdown
   * @description Memoized selector che calcola il conteggio dei dispositivi per ogni tipo di dispositivo
   * tra i `availableDevices` attualmente filtrati.
   * Utilizza `useMemo` per ricalcolare questo riepilogo solo quando `availableDevices` cambia.
   * @returns {Array<Object>} Un array di oggetti, ciascuno contenente le informazioni sul tipo di dispositivo
   * e il conteggio dei dispositivi di quel tipo tra quelli filtrati. Vengono inclusi solo i tipi con un conteggio > 0.
   * @example
   * // Esempio di `deviceTypeBreakdown`:
   * [
   * { id: 1, description: 'Monitor', icon: <MonitorIcon />, count: 5 },
   * { id: 2, description: 'Bilancia', icon: <ScaleIcon />, count: 2 }
   * ]
   */
  const deviceTypeBreakdown = useMemo(() => {
    // Conta i dispositivi per tipo (dtype)
    const counts = availableDevices.reduce((acc, device) => {
      acc[device.dtype] = (acc[device.dtype] || 0) + 1; // Usa dtype per l'aggregazione
      return acc;
    }, {});

    // Mappa i tipi di dispositivo con i loro conteggi
    return configuration.deviceTypes
      .map(type => ({
        ...type,
        count: counts[type.id] || 0, // Assegna il conteggio o 0 se non ci sono dispositivi di quel tipo
      }))
      .filter(type => type.count > 0); // Filtra per mostrare solo i tipi con dispositivi
  }, [availableDevices, configuration.deviceTypes]); // Dipendenze per useMemo

  /**
   * @function handleFilterToggle
   * @description Gestore per l'attivazione/disattivazione dei filtri.
   * Quando un elemento di filtro (es. un gruppo negozio o un tipo dispositivo) viene cliccato,
   * questa funzione aggiunge o rimuove l'ID dell'elemento dalla categoria di filtro corrispondente nello stato `filters`.
   * @param {string} category - La chiave della categoria di filtro da aggiornare (es. 'storeGroups', 'stores', 'deviceGroups', 'deviceTypes').
   * @param {number} id - L'ID dell'elemento di filtro da aggiungere o rimuovere.
   * @example
   * // Cliccando su un checkbox di un gruppo negozio con ID 1:
   * handleFilterToggle('storeGroups', 1);
   */
  const handleFilterToggle = (category, id) => {
    setFilters(prev => {
      const currentSelection = prev[category];
      const newSelection = currentSelection.includes(id)
        ? currentSelection.filter(val => val !== id) // Rimuove se già presente
        : [...currentSelection, id]; // Aggiunge se non presente
      return { ...prev, [category]: newSelection };
    });
  };

  /**
   * @function handleDeviceSelectToggle
   * @description Gestore per la selezione/deselezione di un singolo dispositivo.
   * Aggiunge o rimuove l'ID del dispositivo dallo stato `selectedDevices`.
   * @param {number} deviceId - L'ID del dispositivo da selezionare o deselezionare.
   * @example
   * // Cliccando su un dispositivo con ID 123:
   * handleDeviceSelectToggle(123);
   */
  const handleDeviceSelectToggle = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId) // Rimuove se già selezionato
        : [...prev, deviceId] // Aggiunge se non selezionato
    );
  };
  
  /**
   * @function handleSelectAllVisible
   * @description Gestore per selezionare o deselezionare tutti i dispositivi attualmente visibili
   * (cioè, quelli che corrispondono ai filtri attivi).
   * Se tutti i dispositivi visibili sono già selezionati, li deseleziona tutti.
   * Altrimenti, aggiunge tutti i dispositivi visibili all'elenco dei selezionati.
   * Utilizza un `Set` per evitare duplicati quando si aggiungono dispositivi.
   */
  const handleSelectAllVisible = () => {
    const allVisibleIds = availableDevices.map(d => d.id);
    const areAllSelected = allVisibleIds.every(id => selectedDevices.includes(id));
    if (areAllSelected) {
      // Deseleziona tutti i dispositivi visibili
      setSelectedDevices(prev => prev.filter(id => !allVisibleIds.includes(id)));
    } else {
      // Seleziona tutti i dispositivi visibili, mantenendo quelli già selezionati
      setSelectedDevices(prev => [...new Set([...prev, ...allVisibleIds])]);
    }
  };

  /**
   * @function handleSave
   * @description Gestore richiamato quando il pulsante "Applica Distribuzione" viene cliccato.
   * Chiama la prop `onSave` passando un oggetto contenente l'array degli ID dei dispositivi selezionati,
   * quindi chiude il Dialog.
   */
  const handleSave = () => {
    onSave({playlist: selectedDevices}).then(() => {
      console.log('Dispositivi salvati con successo');
      handleClose(); // Chiude il dialog dopo il salvataggio
    });
  };

  /**
   * @function handleClose
   * @description Gestore richiamato quando il Dialog viene chiuso (tramite il pulsante "Annulla",
   * l'icona di chiusura o il click fuori dal Dialog).
   * Resetta tutti i filtri e le selezioni di dispositivi allo stato iniziale e quindi chiama la prop `onClose`.
   */
  const handleClose = () => {
    // Resetta tutti i filtri, incluso il nuovo per i gruppi dispositivo
    setFilters({ storeGroups: [], stores: [], deviceGroups: [], deviceTypes: [], searchTerm: '' });
    setSelectedDevices([]); // Resetta i dispositivi selezionati
    onClose(); // Chiama la funzione di chiusura passata via props
  };

  // Variabili derivate dallo stato per controllare lo stato dei checkbox "Seleziona tutti"
  const allVisibleSelected = availableDevices.length > 0 && availableDevices.every(d => selectedDevices.includes(d.id));
  const someVisibleSelected = availableDevices.some(d => selectedDevices.includes(d.id));
  
  // Controlla se ci sono filtri attivi per mostrare la sezione "Filtri Attivi"
  const hasActiveFilters = filters.storeGroups.length > 0 || filters.stores.length > 0 || filters.deviceGroups.length > 0 || filters.deviceTypes.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Distribuisci {selectedCount} {selectedCount === 1 ? 'Elemento' : 'Elementi'}
        {/* Pulsante per chiudere il dialog */}
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: 'grey.50' }}>
        <Grid container sx={{ minHeight: '70vh' }}>
          
          {/* PANNELLO DI SINISTRA: FILTRI */}
          <Grid item size={{xs:12, md:6}} sx={{ p: 2, borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
            {/* Campo di ricerca per nome dispositivo o negozio */}
            <TextField fullWidth variant="outlined" size="small" placeholder="Cerca per nome dispositivo o negozio..." value={filters.searchTerm} onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)}}/>
            
            {/* Contenitore per gli Accordion dei filtri, con scroll interno */}
            <Box sx={{ mt: 2, maxHeight: 'calc(70vh - 60px)', overflowY: 'auto' }}>
              {/* Filtro per Gruppo Negozio */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><BusinessIcon sx={{ mr: 1 }} color="action" /><Typography>Filtra per Gruppo Negozio</Typography></AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {/* Mappa i gruppi di negozi disponibili dalla configurazione */}
                    {configuration.storeGroups.map(group => (
                      <ListItem key={group.id} dense button onClick={() => handleFilterToggle('storeGroups', group.id)}>
                        <Checkbox edge="start" checked={filters.storeGroups.includes(group.id)} tabIndex={-1} />
                        <ListItemText primary={group.description} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              {/* Filtro per Negozio */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><StorefrontIcon sx={{ mr: 1 }} color="action" /><Typography>Filtra per Negozio</Typography></AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {/* Mappa i negozi disponibili dalla configurazione */}
                    {configuration.stores.map(store => (
                      <ListItem key={store.id} dense button onClick={() => handleFilterToggle('stores', store.id)}>
                        <Checkbox edge="start" checked={filters.stores.includes(store.id)} tabIndex={-1} />
                        <ListItemText primary={store.description} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              {/* --- NUOVO ACCORDION PER GRUPPI DISPOSITIVO --- */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><DnsIcon sx={{ mr: 1 }} color="action" /><Typography>Filtra per Gruppo Dispositivo</Typography></AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {/* Mappa i gruppi di dispositivi disponibili dalla configurazione */}
                    {configuration.deviceGroups.map(group => (
                      <ListItem key={group.id} dense button onClick={() => handleFilterToggle('deviceGroups', group.id)}>
                        <Checkbox edge="start" checked={filters.deviceGroups.includes(group.id)} tabIndex={-1} />
                        <ListItemText primary={group.description} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              {/* Filtro per Tipo Dispositivo */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><WidgetsIcon sx={{ mr: 1 }} color="action" /><Typography>Filtra per Tipo Dispositivo</Typography></AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {/* Mappa i tipi di dispositivo disponibili (da DEVICE_TYPES aggiunto a configuration) */}
                    {configuration.deviceTypes.map(type => (
                      <ListItem key={type.id} dense button onClick={() => handleFilterToggle('deviceTypes', type.id)}>
                        <Checkbox edge="start" checked={filters.deviceTypes.includes(type.id)} tabIndex={-1} />
                        <ListItemIcon sx={{ minWidth: 32 }}>{type.icon}</ListItemIcon>
                        <ListItemText primary={type.description} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Grid>

          {/* PANNELLO DI DESTRA: LISTA E SELEZIONE DISPOSITIVI */}
          <Grid item size={{xs:12, md:6}} sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box mb={2}>
              {/* Sezione "Filtri Attivi" che mostra i chip dei filtri selezionati */}
              {hasActiveFilters && (
                <Box mb={2}>
                  <Typography variant="overline" color="text.secondary">Filtri Attivi</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {/* Chip per i gruppi di negozi selezionati */}
                    {filters.storeGroups.map(id => <Chip key={id} label={configuration.storeGroups.find(g=>g.id===id)?.description} onDelete={() => handleFilterToggle('storeGroups', id)} size="small" />)}
                    {/* Chip per i negozi selezionati */}
                    {filters.stores.map(id => <Chip key={id} label={configuration.stores.find(s=>s.id===id)?.description} onDelete={() => handleFilterToggle('stores', id)} size="small" />)}
                    {/* NUOVO: Chip per i filtri attivi dei gruppi dispositivo */}
                    {filters.deviceGroups.map(id => <Chip key={id} label={configuration.deviceGroups.find(g=>g.id===id)?.description} onDelete={() => handleFilterToggle('deviceGroups', id)} size="small" />)}
                    {/* Chip per i tipi di dispositivo selezionati, con icona */}
                    {filters.deviceTypes.map(id => <Chip key={id} label={configuration.deviceTypes.find(t=>t.id===id)?.description} onDelete={() => handleFilterToggle('deviceTypes', id)} size="small" icon={configuration.deviceTypes.find(t=>t.id===id)?.icon} />)}
                  </Stack>
                </Box>
              )}
              
              {/* Riepilogo dei dispositivi filtrati per tipo */}
              <Typography variant="overline" color="text.secondary">Riepilogo Dispositivi Filtrati</Typography>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-around', textAlign: 'center', flexWrap: 'wrap', gap: 2 }}>
                {deviceTypeBreakdown.length > 0 ? (
                  // Se ci sono dispositivi filtrati, mostra il conteggio per ogni tipo
                  deviceTypeBreakdown.map(type => (
                    <Box key={type.id}>
                      {/* Clona l'elemento icona per applicare stili specifici */}
                      {React.cloneElement(type.icon, { color: 'primary', sx: { fontSize: 32 } })}
                      <Typography variant="h6" component="div">{type.count}</Typography>
                      <Typography variant="caption">{type.description}</Typography>
                    </Box>
                  ))
                ) : (
                  // Messaggio se nessun dispositivo corrisponde ai filtri
                  <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>Nessun dispositivo corrisponde ai filtri.</Typography>
                )}
              </Paper>
            </Box>
            <Divider sx={{ mb: 1 }} />

            {/* Intestazione della lista dispositivi con "Seleziona tutti" */}
            <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="bold">Dispositivi Corrispondenti ({availableDevices.length})</Typography>
                {/* Checkbox "Seleziona tutti" con stato indeterminate/checked */}
                <FormControlLabel label="Seleziona tutti" sx={{ mr: -1 }} control={<Checkbox checked={allVisibleSelected} indeterminate={someVisibleSelected && !allVisibleSelected} onChange={handleSelectAllVisible} disabled={availableDevices.length === 0} />}/>
              </Box>
            </ListSubheader>
            
            {/* Lista dei dispositivi filtrati e selezionabili */}
            <Paper variant="outlined" sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 'calc(52vh - 60px)' }}>
              {availableDevices.length > 0 ? (
                <List dense>
                  {availableDevices.map(device => {
                    // Trova il nome del negozio e l'icona del tipo di dispositivo per la visualizzazione
                    const storeName = configuration.stores.find(s => s.id === device.store)?.description || 'Negozio non trovato';
                    const deviceIcon = configuration.deviceTypes.find(t => t.id === device.dtype)?.icon || <MonitorIcon />;
                    return (
                      <ListItem key={device.id} dense button onClick={() => handleDeviceSelectToggle(device.id)}>
                        <Checkbox edge="start" checked={selectedDevices.includes(device.id)} tabIndex={-1} />
                        <ListItemIcon sx={{minWidth: 36}}>{deviceIcon}</ListItemIcon>
                        <ListItemText primary={device.description} secondary={storeName} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                // Messaggio se non ci sono dispositivi da visualizzare nella lista
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">Nessun dispositivo da visualizzare.</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        {/* Contatore dei dispositivi selezionati */}
        <Typography sx={{ flexGrow: 1 }} color="text.secondary">{selectedDevices.length} dispositivi selezionati</Typography>
        <Button onClick={handleClose}>Annulla</Button>
        {/* Pulsante per applicare la distribuzione, disabilitato se nessun dispositivo è selezionato */}
        <Button variant="contained" onClick={handleSave} disabled={selectedDevices.length === 0}>Applica Distribuzione</Button>
      </DialogActions>
    </Dialog>
  );
}